<template>
  <Form
    class="configurator-teaser"
    :initial-values="initialValues"
    @submit="onSubmit"
    @invalid-submit="onInvalidSubmit"
    ref="configuratorForm"
    v-slot="context"
  >
    <div
      class="configurator-teaser__editor"
      ref="configurator"
    >
      <h2
        class="configurator-teaser__header"
        v-html="$t('configurator_editor.headline', [originConfiguration.configuratorDisplayName])"
      />

      <template
        v-for="(configuratorService, serviceIndex) in originConfiguration.configuratorServices"
        :key="configuratorService.serviceKey"
      >
        <div
          :id="configuratorService.displayName"
          class="configurator-teaser__configurator"
        >
          <template
            v-for="(attribute, attributeIndex) in configuratorService.attributes"
            :key="attribute.attributeKey"
          >
            <h2
              v-if="attribute.type === 'headline'"
              class="generic_editor_headline"
            >
              {{ attribute.displayName }}
            </h2>

            <template
              v-else-if="attribute.type === 'radio'"
            >
              <radio-picture-select-attribute-editor
                :name="configuratorService.attributes[attributeIndex].attributeKey"
                :label="attribute.displayName"
                :rules="{required: attribute.mandatory}"
                :possible-values="attribute.possibleValues"
                :initial-value="context.meta.initialValues.configuratorServices[serviceIndex].attributes[attribute.attributeKey]"
                @change="resetOnChange"
              />
            </template>

            <template
              v-else-if="attribute.type === 'multi_value_solo_select'"
            >
              <select-vee4
                v-if="attribute.possibleValues"
                :name="configuratorService.attributes[attributeIndex].attributeKey"
                :options="attribute.possibleValues"
                :label="$t('contact.subject')"
                :rules="{required: attribute.mandatory}"
                @change="resetOnChange"
              />
            </template>

            <input-vee4
              v-else
              :name="`configuratorServices[${serviceIndex}].attributes.${attribute.attributeKey}`"
              :type="attribute.type"
              :label="attribute.displayName"
              :rules="{
                required: attribute.mandatory,
                min_value: attribute.minValue,
                max_value: attribute.maxValue,
              }"
              :min="attribute.minValue"
              :max="attribute.maxValue"
              :step="attribute.decimalPlaces ? 1.0 / Math.pow(10, parseInt(attribute.decimalPlaces)) : null"
              @change="resetOnChange"
            />
          </template>
        </div>
      </template>
    </div>

    <div class="configurator-teaser__result">
      <h2 class="configurator-teaser__header">
        {{ $t('configurator_editor.result.headline') }}
      </h2>

      <product-graduated-prices
        class="configurator-teaser__graduated-prices"
        :graduated_prices="graduatedPrices"
        :display_string="$t('configurator_editor.packaging_type')"
        :initial-value="quantity"
        :edit-product="true"
        ref="graduatedPrices"
        :has-price="hasPrice"
      />

      <div
        v-if="totalPrice"
        class="configurator-teaser__result--footer"
      >
        <div class="configurator-teaser__result--footer__wrapper">
          <div class="configurator-teaser__result--footer__headline">
            <span class="amount-price-wrapper--headline">
              {{ $t('configurator_editor.amount') }}
            </span>
            <span class="amount-price-wrapper--headline">
              {{ $t('configurator_editor.total_sum') }}
              <span class="amount-price-wrapper--headline--addition">
                {{ $t('configurator_editor.addition') }}
              </span>
            </span>
          </div>

          <div class="configurator-teaser__result--footer__content">
            <div class="amount-price-wrapper--amount">
              {{ quantity }}
            </div>
            <div class="amount-price-wrapper--price">
              {{ totalPrice }}
            </div>
          </div>
        </div>

        <button
          type="submit"
          id="addToCard"
          class="sale-box__button-to-cart | button button--alt"
          :class="[afterAddedToCardMessage ? 'sale-box__button-to-cart__clicked' : 'sale-box__button-to-cart__not-clicked']"
          name="ADD_TO_CART_BTN_DESKTOP"
          v-html="afterAddedToCardMessage ? $t('global.addedToBasket.desktop'): $t('global.addToBasket')"
        />

        <div class="configurator-teaser__result--footer__additional-information">
          <div class="configurator-teaser__result--footer__delivery">
            <span
              class="configurator-teaser__result--footer__delivery__text"
              v-html="$t('configurator_editor.result.delivery')"
            />
          </div>
          <div
            class="configurator-teaser__result--footer__shipping"
            v-html="$t('configurator_editor.result.shipping')"
          />
        </div>
      </div>
      <div v-else>
        <ErrorMessage
          name="global"
          v-slot="{ message }"
        >
          <general-message
            v-html="message"
            :autoclose="false"
            type="warning"
          />
        </ErrorMessage>

        <button
          type="submit"
          id="calculate"
          class="button button--primary"
        >
          {{ $t('configurator_editor.calculate.button') }}
        </button>
      </div>
    </div>
  </Form>
</template>

<script>
import pigeon from '../../utilities/js/pigeon/pigeon'
import ProductGraduatedPrices from '../productGraduatedPrices/productGraduatedPrices.vue'
import ConfiguratorRepository from './configuratorRepository'
import {ErrorMessage, Form} from 'vee-validate'
import SelectVee4 from '../select/selectVee4.vue'
import InputVee4 from '../inputVue/inputVee4.vue'
import RadioPictureSelectAttributeEditor from './radioPictureSelectAttributeEditor.vue'
import GeneralMessage from '../generalMessage/generalMessage.vue'
import scrollToElement from '../../utilities/js/scrollToElement/scrollToElement'

const graduatedPricesDefaultArray = [
  {'from': '1', 'isReduced': false, 'price': '-', 'reducedPrice': '-', 'savings': '-'},
  {'from': '10', 'isReduced': false, 'price': '-', 'reducedPrice': '-', 'savings': '-'},
  {'from': '50', 'isReduced': false, 'price': '-', 'reducedPrice': '-', 'savings': '-'},
  {'from': '100', 'isReduced': false, 'price': '-', 'reducedPrice': '-', 'savings': '-'},
  {'from': '150', 'isReduced': false, 'price': '-', 'reducedPrice': '-', 'savings': '-'}
]

export default {
  components: {
    GeneralMessage,
    ErrorMessage,
    RadioPictureSelectAttributeEditor,
    InputVee4,
    SelectVee4,
    Form,
    ProductGraduatedPrices
  },
  props: {
    configuration: {
      type: Object,
      required: true
    }
  },
  data() {

    const originConfiguration = this.configuration

    originConfiguration.configuratorServices = Object.values(originConfiguration.configuratorServices)
      .sort((a, b) => a.serviceDisplayOrderNumber - b.serviceDisplayOrderNumber)
    originConfiguration.configuratorServices
      .forEach(configuratorService => configuratorService.attributes
        .forEach(attribute => {
          if (attribute.type === 'integer') {
            attribute.type = 'number'
          } else if (attribute.type === 'boolean') {
            attribute.type = 'checkbox'
          }
        }))

    let configuratorServices = originConfiguration.configuratorServices
      .map(service => {

        const attributes = {}
        service.attributes
          .filter(attribute => attribute.attributeKey !== 'Konfig_Abschnittsueberschrift')
          .forEach(attribute => {
            attributes[attribute.attributeKey] = attribute.defaultValue
              ?? attribute?.possibleValues?.[0].id
              ?? ''
          })

        return {
          serviceKey: service.serviceKey,
          displayName: service.displayName,
          serviceDisplayOrderNumber: service.serviceDisplayOrderNumber,
          attributes: attributes,
        }
      })

    let initialValues = {
      configuratorKey: originConfiguration.configuratorProductId,
      configuratorDisplayName: originConfiguration.configuratorDisplayName,
      configuratorServices: configuratorServices,
    }

    return {
      originConfiguration: originConfiguration,
      initialValues: initialValues,
      configuratorRepository: new ConfiguratorRepository(),
      graduatedPrices: graduatedPricesDefaultArray,
      quantity: undefined,
      totalPrice: undefined,
      afterAddedToCardMessage: false,
    }
  },
  methods: {
    resetOnChange() {
      this.$refs.configuratorForm.setFieldError('global', null)

      this.graduatedPrices = graduatedPricesDefaultArray
      this.totalPrice = undefined
      this.quantity = undefined
    },
    onInvalidSubmit({results}) {
      const firstInvalidInput = this.$el?.[Object.entries(results).find(e => !e[1].valid)?.[0]]
      if (firstInvalidInput) {
        scrollToElement(firstInvalidInput, 20)
      }
    },
    onSubmit(values, actions) {

      actions.setFieldError('global', null)

      if (!this.totalPrice) {
        this.configuratorRepository.calculateConfigurator(values)
          .then((response) => {
            if (response.status === 200 || response.ok) {
              return response.json()
            }
          })
          .then((data) => {
            if (!data) {
              return
            }

            let errors = Object.entries(data.errors)
            if (errors.length) {
              for (const [field, msg] of errors) {
                if (field === 'global') {
                  actions.setFieldError(field, msg)
                  continue
                }

                let fullFieldName

                for (const configuratorService of values.configuratorServices) {
                  if (configuratorService.attributes?.[field]) {
                    let i = configuratorService.serviceDisplayOrderNumber - 1
                    fullFieldName = `configuratorServices[${i}].attributes.${field}`
                    break
                  }
                }

                if (fullFieldName) {
                  actions.setFieldError(fullFieldName, msg)

                  const firstInvalidInput = this.$el?.[fullFieldName]
                  if (firstInvalidInput) {
                    scrollToElement(firstInvalidInput, 20)
                  }
                }
              }
            } else {
              this.graduatedPrices = data.graduation
              this.totalPrice = data.totalAmount
              this.quantity = data.quantity
            }
          })
          .catch(reason => {
            pigeon.publish('GlobalAppError:show', {error: `Server responded with status ` + reason})
            actions.setFieldError('global', $t('validation.configurator.default'))
          })
        return
      }

      this.configuratorRepository.addToCart(values)
        .then((response) => {
          if (response.status === 200 || response.ok) {
            this.afterAddedToCardMessage = true
            setTimeout(() => {
              this.afterAddedToCardMessage = false
            }, 3000)

            return response.json()
          }
        })
        .then((res) => this.$store.dispatch('productPage/updateCart', Object.assign(res, {
          'boxIndex': res.boxIndex,
          'itemId': res.itemId,
          'isAddedToCart': true
        })))
        .catch(reason => {
          pigeon.publish('GlobalAppError:show', {error: `Server responded with status ` + reason})
          actions.setFieldError('global', this.$t('validation.configurator.default'))
        })
    },
  },
  computed: {
    hasPrice() {
      return this.totalPrice !== undefined
    }
  }
}

</script>
